/**
 * Mentions Service
 *
 * Lightweight service for mention-specific operations:
 * - Searching mentionable users with role-based sorting
 * - Creating push notifications for @mentioned users
 *
 * This service is generic and can be used by any chat context.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import { searchUsers } from './admin';
import type { MentionSuggestion, MentionChatType } from '@/types/mentions';
import type { UserRole } from '@/types';

// ==================== User Search ====================

/** Role priority for sorting: admins first, then leaders, then members */
const ROLE_PRIORITY: Record<string, number> = {
  admin: 0,
  leader: 1,
  member: 2,
};

/**
 * Search for mentionable users, sorted by role (admins/leaders first).
 *
 * @param query - Search query string (matches displayName, email, hebrewName)
 * @param chatType - Chat context (affects filtering logic)
 * @param currentUserId - Current user's ID (excluded from results)
 * @returns Sorted array of mention suggestions
 */
export async function searchMentionableUsers(
  query: string,
  _chatType: MentionChatType,
  currentUserId: string
): Promise<MentionSuggestion[]> {
  if (!query.trim()) return [];

  const users = await searchUsers(query);

  // Filter out current user and banned/suspended users
  const filtered = users.filter(
    (u) => u.uid !== currentUserId && (u.status || 'active') === 'active'
  );

  // Sort by role priority (admins/leaders first), then alphabetically
  const sorted = filtered.sort((a, b) => {
    const roleA = ROLE_PRIORITY[a.role || 'member'] ?? 2;
    const roleB = ROLE_PRIORITY[b.role || 'member'] ?? 2;
    if (roleA !== roleB) return roleA - roleB;
    return (a.displayName || '').localeCompare(b.displayName || '');
  });

  // Map to MentionSuggestion format
  return sorted.map((u) => ({
    userId: u.uid,
    displayName: u.displayName || u.email || 'Unknown',
    hebrewName: u.hebrewName || undefined,
    photoURL: u.photoURL || undefined,
    role: (u.role || 'member') as UserRole,
  }));
}

// ==================== Push Notifications ====================

/**
 * Create push notification entries for @mentioned users.
 *
 * Only creates notifications for leaders and admins (members don't have
 * access to Leaders Chat and shouldn't be notified). Skips the sender.
 *
 * @param data - Mention notification data
 */
export async function createMentionPushNotifications(data: {
  senderId: string;
  senderName: string;
  messageId: string;
  mentionedUserIds: string[];
  /** Roles of mentioned users (for filtering - only notify leaders/admins) */
  mentionedUserRoles?: Record<string, UserRole>;
  chatType: MentionChatType;
}): Promise<void> {
  const pushQueueRef = collection(db, `artifacts/${appId}/push_queue`);

  // Filter: only notify leaders/admins, not the sender, not members
  const recipientIds = data.mentionedUserIds.filter((userId) => {
    // Never notify the sender about their own mention
    if (userId === data.senderId) return false;

    // If we have role info, only notify leaders/admins
    if (data.mentionedUserRoles) {
      const role = data.mentionedUserRoles[userId];
      if (role === 'member') return false;
    }

    return true;
  });

  if (recipientIds.length === 0) return;

  // Determine URL based on chat type
  const url = data.chatType === 'leaders' ? '/chat/leaders' : '/chat/community';

  const promises = recipientIds.map(async (recipientId) => {
    await addDoc(pushQueueRef, {
      type: 'mention',
      recipientId,
      title: `@mention from ${data.senderName}`,
      body: `${data.senderName} mentioned you in ${data.chatType === 'leaders' ? 'Leaders Chat' : 'Community Chat'}`,
      data: {
        messageId: data.messageId,
        senderId: data.senderId,
        chatType: data.chatType,
        url,
      },
      createdAt: serverTimestamp(),
    });
  });

  await Promise.all(promises);
}
